
import moment from 'moment'
import numbro from 'numbro'

import { mapActions, mapGetters } from 'vuex'
import SalesDecomposition from '@/components/simulationDetails/SalesDecomposition.vue'
import MediaSpendAndReturn from '@/components/simulationDetails/MediaSpendAndReturn.vue'
import SalesBySegment from '@/components/simulationDetails/SalesBySegment.vue'
import ChannelCurvesChartMulti from '@/components/simulationDetails/ChannelCurvesChartMulti.vue'
import TextBox from '@/components/TextBox.vue'
import TextBoxMetric from '@/components/TextBoxMetric.vue'
import QualityGauge from '@/components/QualityGauge.vue'
import MicroaudienceComposition from '@/components/MicroaudienceComposition.vue'
import MicroaudienceDifference from '@/components/MicroaudienceDifference.vue'
import AiSummary from '@/components/AI/AiSummary.vue'
import SaturationCurves from '@/components/SaturationCurves.vue'

// Model Report v2
import RiverChart from '@/components/RiverChart.vue'
import ScatterChart from '@/components/ScatterChart.vue'
import BarChart from '@/components/BarChart.vue'
import StackedBarChart from '@/components/StackedBarChart.vue'

// Other
import ModelAccessManager from '@/components/ModelAccessManager.vue'
import HiddenSectionsReport from '@/components/HiddenSectionsReport.vue'
import ModelVersionBadge from '@/components/ModelVersionBadge.vue'
import NgtorchModelChips from '@/components/NgtorchModelChips.vue'
import { prettyNames } from '@/mixins/prettyNames.js'
import { loadModelReportFromClient } from '@/utils/modelReport.js'
import { roasUnitLabel, roasSuffix, roasMantissa } from '@/utils/roasUnits.js'
import { hiddenSaturationSections } from '@/utils/saturationSections.js'
import { copyTextToClipboard } from '@/utils/clipboard.js'
import { hiddenKpis, hasHiddenKpis, isKpiHidden, firstVisibleOutputName } from '@/utils/hiddenKpis.js'
import { combineOutputs } from '@/utils/combineOutputs.js'
import { defaultComponentDictionary, mergeComponentDictionary } from '@/utils/componentDictionary.js'
import {
  seriesMapToRows,
  seriesToRows,
  modelFitToRows,
  componentsToRows,
  byFreqToRows,
  mediaByFreqToRows,
  saturationCurvesToRows,
  saturationDatapointsToRows,
  decayCurvesToRows,
  segmentsToRows,
  namesValuesToRows,
  microaudienceCompositionToRows,
  buildPerKpi,
  downloadPerKpi
} from '@/utils/modelReportXlsx.js'

// The KPI selector's value for a custom subset of KPIs, whose sum the report shows like the Total.
const CUSTOM_OUTPUT = '__custom__'

export default {
  data () {
    return {
      currentOutput: null,
      // The KPIs added up when the selector is on CUSTOM_OUTPUT.
      customOutputs: [],
      currentTab: 0,
      manageModelAccessModal: false,
      isEditingAISummary: false,
      aiSummaryVersions: [],
      selectedVersionIndex: 0,
      editedAISummary: null, // Will be initialized in created() with translated text
      shareModelReportDialog: false,
      shareModelReportEnabled: false,
      shareModelReportLink: null,
      shareModelReportLinkCopied: false,
      shareModelReportId: null,
      downloadingXlsx: false,
      downloadXlsxFailed: false,
      modelFitGranularityOverride: null,
      roasAveragePeriodGranularity: null,
      roasAverageMediaGranularity: null,
      selectedInputVariable: null,
      // Currently selected first-level decay group (null when decay is not grouped).
      selectedDecayGroup: null,
      // Currently selected sales-decomposition drill-down group. Defaults to the full
      // "All Variables" view; only meaningful when the report uses the nested structure.
      selectedSalesDecompositionGroup: 'All_Variables',
      selectedSalesDecompositionGroupWeekly: 'All_Variables',
      fetchedModelReport: null,
      fetchedModelReportFor: null,
      moment
    }
  },
  mixins: [prettyNames],
  props: {
    shareParams: {
      type: Object,
      required: false
    }
  },
  methods: {
    ...mapActions('clients', [
      'loadClients',
      'setCurrentClient'
    ]),
    ...mapActions('shared', [
      'getShareModelReportLink',
      'deleteShareModelReportLink'
    ]),
    normalizeByFreq (o) {
      if (!o || typeof o !== 'object') return null
      const aliasMap = {
        weekly: 'weekly',
        week: 'weekly',
        w: 'weekly',
        monthly: 'monthly',
        month: 'monthly',
        m: 'monthly',
        ms: 'monthly',
        quarterly: 'quarterly',
        quarter: 'quarterly',
        q: 'quarterly',
        qs: 'quarterly',
        yearly: 'yearly',
        year: 'yearly',
        y: 'yearly',
        ys: 'yearly',
        annual: 'yearly',
        a: 'yearly'
      }
      const out = {}
      for (const rawKey of Object.keys(o)) {
        const v = o[rawKey]
        if (!v) continue
        const canonical = aliasMap[String(rawKey).toLowerCase()]
        if (canonical && !(canonical in out)) {
          out[canonical] = v
        }
      }
      return out
    },
    // Finds the actual "All_Variables" key inside a sales-decomposition object, tolerating any
    // capitalization variant the report JSON might use (All_Variables, all_variables, AlL_vAriables, …).
    // Returns the real key as written in the JSON (so callers can index with it), or null if absent.
    findAllVariablesKey (o) {
      if (!o || typeof o !== 'object') return null
      return Object.keys(o).find(key => String(key).toLowerCase() === 'all_variables') || null
    },
    aggregateSalesDecompositionByPeriod (byPeriod, mode = 'sum') {
      if (!byPeriod || typeof byPeriod !== 'object') {
        return { components: [], total: 0 }
      }
      const components = Object.keys(byPeriod).map(name => {
        const ys = (byPeriod[name] && Array.isArray(byPeriod[name].y)) ? byPeriod[name].y : []
        const sum = ys.reduce((a, b) => a + (typeof b === 'number' ? b : 0), 0)
        const value = mode === 'average' && ys.length > 0 ? sum / ys.length : sum
        return { name, value }
      })
      const total = components.reduce((a, c) => a + c.value, 0)
      return { components, total }
    },
    // Label for a component name: its custom name from Settings > Custom names > Component
    // names, or the raw name when it has none.
    componentDisplayName (name) {
      return this.componentPrettyNamesDict[name] || name
    },
    // Label for a decomposition/decay group key. Same as componentDisplayName except for
    // "Instant Marketing", which is always shown as "Marketing" even when a custom name is
    // configured for it. That is deliberate: the PM team asked for that wording explicitly
    // during the "Mejoras App" project, so the custom name does NOT override it here.
    decompGroupDisplayName (key) {
      if (key === 'Instant Marketing') return 'Marketing'
      return this.componentDisplayName(key)
    },
    // Builds the drill-down selector items for a nested sales_decomposition_by_period object.
    // Returns [] for legacy (flat) reports that lack the "All_Variables" sub-key, so the
    // selector stays hidden. "All_Variables" is forced first (the default view), followed by
    // the "Instant Marketing" drill-down group surfaced as simply "Marketing" — deliberately,
    // and even when a custom name is configured for it: the PM team asked for that wording
    // explicitly during the "Mejoras App" project — then the remaining drillable components
    // pretty-translated via the client's components dictionary.
    buildDecompGroupItems (byPeriod) {
      const allVariablesKey = this.findAllVariablesKey(byPeriod)
      if (!byPeriod || typeof byPeriod !== 'object' || !allVariablesKey) {
        return []
      }
      const items = [{ text: this.$t('components.ModelReportComponent.salesDecompositionAllVariables'), value: allVariablesKey }]
      const marketingKey = Object.keys(byPeriod).find(key => String(key).toLowerCase().includes('instant marketing'))
      if (marketingKey) {
        items.push({ text: 'Marketing', value: marketingKey })
      }
      Object.keys(byPeriod).forEach(key => {
        if (key === allVariablesKey || key === marketingKey) return
        items.push({ text: this.componentDisplayName(key), value: key })
      })
      return items
    },
    // Resolves the data/colors/prettyNames a StackedBarChart should render for the given
    // sales-decomposition object and selected group. A drilled component renders its members -
    // media channels under Instant Marketing, components under the rest - named by
    // decompDrillPrettyNamesDict, with channel colors for the ones that are channels.
    buildDecompChartConfig (byPeriod, selectedGroup) {
      const allVariablesKey = this.findAllVariablesKey(byPeriod)
      if (!byPeriod || typeof byPeriod !== 'object' || !allVariablesKey) {
        // Legacy (flat) report: currentOutputObject already aliased these component names on the
        // way in, so there is nothing left to translate here.
        return { data: byPeriod || {}, colors: this.client ? this.client.colors : null, prettyNames: {} }
      }
      const group = (selectedGroup && selectedGroup in byPeriod) ? selectedGroup : allVariablesKey
      if (group === allVariablesKey) {
        // Nested report: currentOutputObject only reaches the drill-down groups, one level above
        // these, so the components arrive raw and are aliased here.
        return {
          data: byPeriod[allVariablesKey],
          colors: this.client ? this.client.colors : null,
          prettyNames: this.componentPrettyNamesDict
        }
      }
      const data = byPeriod[group]
      const mediaDict = this.client && this.client.pretty_names && this.client.pretty_names.media
        ? this.client.pretty_names.media
        : {}
      const fallbackColors = (this.client && this.client.colors) ? this.client.colors : []
      // Colors are keyed by the channel's PRETTY name (channelColors is built from the media
      // dictionary), so only a media channel can match one; anything else takes the palette.
      const colors = Object.keys(data).map((rawKey, index) => {
        const prettyKey = mediaDict[rawKey] || rawKey
        if (this.channelColors && this.channelColors[prettyKey]) {
          return this.channelColors[prettyKey]
        }
        return fallbackColors.length ? fallbackColors[index % fallbackColors.length] : null
      })
      return { data, colors, prettyNames: this.decompDrillPrettyNamesDict }
    },
    // AI Summary
    generateNewVersion () {
      // Generate a random text version
      const templateParams = {
        dateFrom: this.formatDate(this.date_from),
        dateTo: this.formatDate(this.date_to),
        spend: this.formatValue(this.spend),
        spendUnit: this.spendUnitName,
        sales: this.formatValue(this.sales),
        salesUnit: this.currentOutputUnitName,
        kpi: this.customKpiName
      }
      const templates = [
        this.$t('components.ModelReportComponent.aiSummaryTemplate1', templateParams),
        this.$t('components.ModelReportComponent.aiSummaryTemplate2', templateParams),
        this.$t('components.ModelReportComponent.aiSummaryTemplate3', templateParams)
      ]
      const randomTemplate = templates[Math.floor(Math.random() * templates.length)]
      // Add new version
      this.aiSummaryVersions.push({
        text: randomTemplate,
        timestamp: new Date(),
        isFavorite: false
      })
      // Select newest version
      this.selectedVersionIndex = this.aiSummaryVersions.length - 1
    },
    toggleFavorite () {
      // Remove favorite status from all versions
      this.aiSummaryVersions.forEach(version => {
        version.isFavorite = false
      })
      // Toggle favorite status for selected version
      this.aiSummaryVersions[this.selectedVersionIndex].isFavorite = true
    },
    saveEdit () {
      this.isEditingAISummary = false
      // Add new version based on the edited text
      this.aiSummaryVersions.push({
        text: this.editedAISummary,
        timestamp: new Date(),
        baseVersion: this.selectedVersionIndex, // Track which version this was based on
        isFavorite: false
      })
      // Select newest version
      this.selectedVersionIndex = this.aiSummaryVersions.length - 1
    },
    startEdit () {
      this.isEditingAISummary = true
      // Initialize edited text with currently selected version
      this.editedAISummary = this.aiSummaryVersions[this.selectedVersionIndex].text
    },
    cancelEdit () {
      this.isEditingAISummary = false
      // Reset to currently selected version
      this.editedAISummary = this.aiSummaryVersions[this.selectedVersionIndex].text
    },
    selectVersion (index) {
      this.selectedVersionIndex = index
      this.editedAISummary = this.aiSummaryVersions[index].text
    },
    manageModelAccess () {
      this.manageModelAccessModal = true
    },
    formatDate (date) {
      return moment(date).format('MMM YYYY')
    },
    formatValue: function (value) {
      return numbro(value).format({
        average: true,
        lowPrecision: false,
        mantissa: 1,
        optionalMantissa: true
      })
    },
    injectChanges () {
      try {
        this.client.model_report = JSON.parse(this.$refs['raw_model_report'].internalValue)
      } catch (error) {
        console.log(error)
      }
    },

    // Share Model Report methods
    async openDialogShareModelReport () {
      this.shareModelReportDialog = true
      // Check if there's already a share link for this model report
      const result = await this.getShareModelReportLink({
        clientId: this.client.id,
        uid: this.authUser.uid,
        hostname: window.location.hostname,
        generate: false
      })
      if (result && result.link) {
        this.shareModelReportEnabled = true
        this.shareModelReportLink = result.link
        this.shareModelReportId = result.shareId
      } else {
        this.shareModelReportEnabled = false
        this.shareModelReportLink = null
        this.shareModelReportId = null
      }
    },
    async toggleShareModelReport () {
      if (this.shareModelReportEnabled) {
        // Enable sharing - generate link
        const result = await this.getShareModelReportLink({
          clientId: this.client.id,
          uid: this.authUser.uid,
          hostname: window.location.hostname,
          generate: true
        })
        if (result && result.link) {
          this.shareModelReportLink = result.link
          this.shareModelReportId = result.shareId
        }
      } else {
        // Disable sharing - delete link
        if (this.shareModelReportId) {
          await this.deleteShareModelReportLink({
            shareId: this.shareModelReportId
          })
          this.shareModelReportLink = null
          this.shareModelReportId = null
        }
      }
    },
    copyShareModelReportLinkToClipboard () {
      if (this.shareModelReportLink) {
        // The snackbar is raised only once the text is really on the clipboard:
        // it used to be raised unconditionally, so it announced a copy that had
        // not happened every time writeText was denied.
        copyTextToClipboard(this.shareModelReportLink)
          .then(copied => {
            if (copied) {
              this.shareModelReportLinkCopied = true
            } else {
              console.error('Failed to copy link')
            }
          })
      }
    },

    // XLSX download: the whole report, one sheet per section, in one workbook per KPI of the
    // selector (a ZIP of them when there is more than one). SheetJS is loaded only on click so it
    // stays out of the route's bundle.
    async downloadReportXlsx () {
      if (this.downloadingXlsx || !this.parsedModelReport) return
      this.downloadingXlsx = true
      try {
        // The custom subset gets a workbook too while it adds up two or more KPIs.
        const offered = this.outputNames.filter(name => name !== CUSTOM_OUTPUT ||
          (this.isCustomOutput && this.customOutputNames.length >= 2))
        const kpis = offered.length > 1 ? offered : [this.currentOutput]
        const books = buildPerKpi(kpis, this.currentOutput, kpi => { this.currentOutput = kpi }, () => this.buildXlsxSheets())
        const XLSX = await import('xlsx')
        const period = [this.date_from, this.date_to].filter(Boolean).join('_')
        const label = kpi => kpi === CUSTOM_OUTPUT ? this.customOutputLabel : kpi
        downloadPerKpi(XLSX, books, kpi => [this.client.name, 'Model Report', label(kpi), period].filter(Boolean).join(' - '))
      } catch (error) {
        console.error('Failed to download the model report as XLSX:', error)
        this.downloadXlsxFailed = true
      } finally {
        this.downloadingXlsx = false
      }
    },
    // The report's sections as [{ title, suffix, rows }], in the order the report shows them.
    // A section goes in when the report shows it to this user (the same show* computeds the
    // template reads), except that both the monthly and the weekly variant go in when the report
    // has both, whichever the selectors are on. Sections without data are left out.
    buildXlsxSheets () {
      const t = (key, params) => this.$t('components.ModelReportComponent.' + key, params || {})
      const v2 = this.v2Report || {}
      const kpi = this.customKpiName
      const kpiHeader = `${kpi} (${this.currentOutputUnitName})`
      const spendHeader = `${t('spend')} (${this.spendUnitName})`
      const roiUnit = v2.roi_unit_name === 'USD/USD' ? this.spendUnitName + '/' + this.spendUnitName : v2.roi_unit_name
      const roasHeader = roiUnit ? `${t('roas')} (${roiUnit})` : t('roas')
      const dateHeader = t('xlsxDate')
      const channelHeader = t('xlsxChannel')
      const groupHeader = t('xlsxGroup')
      const freqs = ['weekly', 'monthly', 'quarterly', 'yearly']
      const freqLabel = freq => t(freq)
      const groupLabel = group => this.decompGroupDisplayName(group)
      const mediaDict = this.client.pretty_names && this.client.pretty_names.media ? this.client.pretty_names.media : {}
      // Only the monthly paths are pretty-translated by currentOutputObject; translating again is a no-op.
      const prettyMedia = map => (map && typeof map === 'object') ? this.prettyTranslateNames(map, [''], mediaDict) : null

      const sheets = []
      const add = (title, suffix, rows) => {
        if (rows) sheets.push({ title: title, suffix: suffix, rows: rows })
      }
      // A section with a monthly and a weekly variant: one sheet each, named by granularity only
      // when there is a weekly one to tell apart.
      const addByGranularity = (title, monthlyData, weeklyData, build) => {
        const hasWeekly = !!(weeklyData && typeof weeklyData === 'object' && Object.keys(weeklyData).length)
        if (monthlyData) add(title, hasWeekly ? t('monthly') : null, build(monthlyData))
        if (hasWeekly) add(title, t('weekly'), build(weeklyData))
      }

      // Summary
      const summary = [
        [t('xlsxMetric'), t('xlsxValue')],
        [t('xlsxModel'), this.client.name],
        [t('kpi'), this.currentOutputLabel || kpi],
        [t('xlsxStartDate'), this.date_from],
        [t('xlsxEndDate'), this.date_to],
        [spendHeader, this.spend],
        [kpiHeader, this.sales],
        [this.roasTotalLabel, this.roasTotal]
      ]
      if (this.showModelQuality && v2.model_quality) {
        summary.push([t('mape'), v2.model_quality.MAPE])
        summary.push([t('rae'), v2.model_quality.RAE])
        summary.push([t('correlation'), v2.model_quality.Correlation])
      }
      add(t('summary'), null, summary)

      // Summary tab sections
      if (this.showModelFit) {
        addByGranularity(t('modelFit'), v2.model_fit, this.hasModelFitWeekly ? v2.model_fit_weekly : null,
          raw => modelFitToRows(raw, dateHeader))
      }
      if (this.hasTotalRoasByMedia) {
        add(t('roas'), null, seriesToRows(this.totalRoasByMediaChartData, channelHeader, roasHeader))
      }
      const decompositionDictionary = mergeComponentDictionary(
        defaultComponentDictionary(key => this.$t(key)),
        this.client.pretty_names ? this.client.pretty_names.components : null
      )
      const decompositionName = name => decompositionDictionary[name] || name
      let decomposition = null
      if (this.salesDecompositionV2Data) {
        decomposition = this.salesDecompositionV2Data.components.map(c => ({ name: decompositionName(c.name), value: c.value }))
      } else if (this.currentOutputObject && this.currentOutputObject.components) {
        const components = this.currentOutputObject.components
        decomposition = Object.keys(components).map(name => ({ name: decompositionName(name), value: components[name] }))
      }
      add(this.$t('components.simulationDetails.SalesDecomposition.salesDecomposition', { kpi: kpi }), null,
        componentsToRows(decomposition, t('xlsxComponent'), kpiHeader, this.$t('components.simulationDetails.SalesDecomposition.total'), this.sales))
      if (this.hasMediaSpendAndReturnData) {
        const data = this.mediaSpendAndReturnData
        const rows = [[channelHeader, spendHeader, t('xlsxAttributed', { kpi: kpi }) + ` (${this.currentOutputUnitName})`, t('roas')]]
        data.channels.forEach(channel => {
          const spend = data.spend[channel]
          const attributed = data.attributedSales[channel]
          rows.push([channel, spend, attributed, spend ? attributed / spend : null])
        })
        add(t('mediaSpendAndReturn'), null, rows)
      }
      if (this.showRoasAveragePeriod) {
        add(t('averageRoasByPeriod'), null,
          byFreqToRows(this.normalizedRoasAverageByFreq, freqs, freqLabel, [t('xlsxGranularity'), dateHeader, roasHeader]))
      } else if (this.showRoasAveragePeriodLegacy) {
        add(t('quarterlyAverageRoas'), null, seriesToRows(v2.quarterly_roi_average_total, dateHeader, roasHeader))
      }

      // Drivers Performance tab sections
      const decompositionByPeriodRows = byPeriod => {
        const config = this.buildDecompChartConfig(byPeriod, null)
        // A flat weekly report reaches here raw (currentOutputObject only aliases the monthly one).
        const columns = Object.keys(config.data || {}).map(key => ({ key: key, label: config.prettyNames[key] || this.componentDisplayName(key) }))
        return seriesMapToRows(config.data, dateHeader, columns)
      }
      if ('sales_decomposition_by_period' in v2 || 'sales_decomposition_by_period_weekly' in v2) {
        addByGranularity(t('salesDecompositionByPeriod', { kpi: kpi }), v2.sales_decomposition_by_period,
          v2.sales_decomposition_by_period_weekly, decompositionByPeriodRows)
      }
      if (!this.salesDecompositionHasAllVariables) {
        addByGranularity(t('seasonalityEffects'), v2.seasonality, v2.seasonality_weekly,
          data => seriesMapToRows(data, dateHeader))
      }
      addByGranularity(t('mediaMix'), v2.media_mix, v2.media_mix_weekly,
        data => seriesMapToRows(prettyMedia(data), dateHeader))
      if (!this.salesDecompositionHasAllVariables) {
        addByGranularity(t('mediaAttributionByPeriod'), v2.media_attributed_sales_decomposition_by_period,
          v2.media_attributed_sales_decomposition_by_period_weekly, data => seriesMapToRows(prettyMedia(data), dateHeader))
      }
      addByGranularity(t('mediaSpend'), v2.media_spend, v2.media_spend_weekly,
        data => seriesMapToRows(prettyMedia(data), dateHeader))
      if (this.hasInputsOverTime) {
        // Every variable in one sheet, one column per category, named "variable - category".
        const columns = {}
        Object.keys(this.inputsOverTime).forEach(variable => {
          const categories = this.inputsOverTime[variable] && this.inputsOverTime[variable].categories
          if (!categories) return
          Object.keys(categories).forEach(category => {
            columns[`${this.componentDisplayName(variable)} - ${category}`] = categories[category]
          })
        })
        add(t('inputOverTime'), null, seriesMapToRows(columns, dateHeader))
      }
      if (this.showRoasAverageMedia) {
        add(t('averageRoasByMedia'), null,
          mediaByFreqToRows(prettyMedia(this.normalizedRoasAverageMediaByFreq), freqs, freqLabel, t('xlsxGranularity'), dateHeader))
      } else if (this.showRoasAverageMediaLegacy) {
        add(t('quarterlyAverageRoasByMedia'), null, seriesMapToRows(v2.quarterly_roi_average_media, dateHeader))
      }
      if (this.showSaturationCurves) {
        const saturationTitle = this.$t('components.SaturationCurves.saturationCurves')
        add(saturationTitle, null, saturationCurvesToRows(this.saturationCurvesData, groupLabel,
          [groupHeader, channelHeader, spendHeader, kpiHeader, t('xlsxMarginalRoas')]))
        add(saturationTitle, t('xlsxDatapoints'), saturationDatapointsToRows(this.saturationCurvesData, groupLabel,
          [groupHeader, channelHeader, dateHeader, spendHeader, kpiHeader]))
      }
      add(t('decayCurves'), null,
        decayCurvesToRows(this.decayCurvesData, groupLabel, groupHeader, `${t('xlsxLag')} (${this.decayChartGranularity})`))
      if (this.showPricingEffect) {
        const pricing = v2.effects.pricing
        add(t('pricingEffect'), null, seriesToRows(pricing, pricing.x_unit_name || 'Price Value', 'Price Effect'))
      }
      if (this.showDistributionEffect) {
        const distribution = v2.effects.distribution
        add(t('distributionEffect'), null, seriesToRows(distribution, distribution.x_unit_name || 'Distribution Value', 'Distribution Effect'))
      }

      // Segments Detail tab sections
      if (this.showSalesBySegment) {
        add(this.$t('components.simulationDetails.SalesBySegment.totalSalesBySegment', { kpi: kpi }), null,
          segmentsToRows(this.parsedModelReport.segments, [t('xlsxSegment'), t('xlsxLabel'), t('xlsxValue')]))
      }
      if (this.showMicroaudienceDifference) {
        add(this.$t('components.MicroaudienceDifference.title'), null,
          namesValuesToRows(this.parsedModelReport.microaudience.difference, [t('xlsxName'), t('xlsxValue')]))
      }
      if (this.showMicroaudienceComposition) {
        add(this.$t('components.MicroaudienceComposition.title'), null,
          microaudienceCompositionToRows(this.parsedModelReport.microaudience, t('xlsxName'), this.$t('components.MicroaudienceComposition.projectedWave')))
      }
      return sheets
    }
  },
  computed: {
    ...mapGetters('auth', [
      'authUser',
      'isModelAccessManager',
      'isExternalUser'
    ]),
    ...mapGetters('clients', [
      'clients',
      'currentClient',
      'currentClientObject'
    ]),
    modelReportFetchKey () {
      // Returns a stable string that identifies the Storage-backed report version,
      // so the watcher refetches whenever the server uploads a new file.
      if (!this.client || !this.client.model_report_storage) return null
      const ts = this.client.model_report_updated_at
      let tsKey = ''
      if (ts) {
        if (typeof ts.toMillis === 'function') tsKey = String(ts.toMillis())
        else if (ts.seconds !== undefined) tsKey = ts.seconds + '.' + (ts.nanoseconds || 0)
        else tsKey = String(ts)
      }
      return this.client.model_report_storage + '@' + tsKey
    },
    initialAISummary () {
      if (!this.parsedModelReport || !this.date_from || !this.date_to) {
        return this.$t('components.ModelReportComponent.aiSummaryInitial', { kpi: this.customKpiName })
      }
      return this.$t('components.ModelReportComponent.aiSummaryInitialFull', {
        kpi: this.customKpiName,
        dateFrom: this.formatDate(this.date_from),
        dateTo: this.formatDate(this.date_to),
        spend: this.formatValue(this.spend),
        spendUnit: this.spendUnitName,
        sales: this.formatValue(this.sales),
        salesUnit: this.currentOutputUnitName
      })
    },
    customKpiName () {
      if (this.client.customKpiName) {
        return this.client.customKpiName
      } else {
        return this.$t('components.ModelReportComponent.defaultKpiName')
      }
    },
    showSalesDecompositionPercentages () {
      if (this.client.hideSalesDecompositionPercentages) {
        return false
      } else {
        return true
      }
    },
    // -------------------------------------------------------------------------------------
    // Section visibility. Every v-if in the template reads one of these, and hiddenSections
    // below negates these same computeds, so the Admin tab's list cannot drift from what is
    // actually rendered. The Drivers Performance ones are branch-aware: they answer for the
    // weekly or the monthly variant depending on hasWeeklyDetailData, which is what decides
    // which of the two blocks the template renders.
    // -------------------------------------------------------------------------------------
    showManageAccessButton () {
      return !this.shareParams && (this.authUser.isAdmin || this.authUser.isManager || this.authUser.isSupervisor || this.isModelAccessManager)
    },
    showAiSummary () {
      return !!(this.client && this.parsedModelReport && (this.authUser.isScientist || this.authUser.isAdmin) && this.client.enableAI)
    },
    showSegmentsTab () {
      return !!(this.parsedModelReport && (this.parsedModelReport.segments || this.parsedModelReport.microaudience))
    },
    showAdminTab () {
      return !this.shareParams && (this.authUser.isAdmin || this.authUser.isScientist)
    },
    showModelFit () {
      return !!(this.v2Report && 'model_fit' in this.v2Report && this.client.type !== 'ekrm')
    },
    showModelQuality () {
      return !!(this.v2Report && 'model_quality' in this.v2Report && this.client.type !== 'ekrm' && !this.isExternalUser)
    },
    showRoasAveragePeriod () {
      return !!(this.hasRoasAverageByFreq && this.v2Report && 'roi_unit_name' in this.v2Report)
    },
    showRoasAveragePeriodLegacy () {
      return !!(this.v2Report && 'quarterly_roi_average_total' in this.v2Report && 'roi_unit_name' in this.v2Report)
    },
    showSalesDecompositionByPeriod () {
      if (this.hasWeeklyDetailData) {
        return !!(this.v2Report && 'sales_decomposition_by_period_weekly' in this.v2Report && this.v2Report.sales_decomposition_by_period_weekly)
      }
      return !!(this.v2Report && 'sales_decomposition_by_period' in this.v2Report)
    },
    showSalesDecompositionDrillDown () {
      return this.hasWeeklyDetailData ? this.salesDecompositionWeeklyHasAllVariables : this.salesDecompositionHasAllVariables
    },
    showSeasonalityEffects () {
      if (this.hasWeeklyDetailData) {
        return !!(this.v2Report && 'seasonality_weekly' in this.v2Report && this.v2Report.seasonality_weekly && !this.salesDecompositionHasAllVariables)
      }
      return !!(this.v2Report && 'seasonality' in this.v2Report && !this.salesDecompositionHasAllVariables)
    },
    showMediaMix () {
      if (this.hasWeeklyDetailData) {
        return !!(this.v2Report && 'media_mix_weekly' in this.v2Report && this.v2Report.media_mix_weekly && !(this.hasInputsOverTime && Object.keys(this.weeklyInputChartData).length))
      }
      return !!(this.v2Report && 'media_mix' in this.v2Report && !(this.hasInputsOverTime && Object.keys(this.monthlyInputChartData).length))
    },
    showMediaAttributionByPeriod () {
      if (this.hasWeeklyDetailData) {
        return !!(this.v2Report && 'media_attributed_sales_decomposition_by_period_weekly' in this.v2Report && this.v2Report.media_attributed_sales_decomposition_by_period_weekly && !this.salesDecompositionHasAllVariables)
      }
      return !!(this.v2Report && 'media_attributed_sales_decomposition_by_period' in this.v2Report && !this.salesDecompositionHasAllVariables)
    },
    // The Input Over Time data of the branch being rendered.
    inputChartData () {
      return this.hasWeeklyDetailData ? this.weeklyInputChartData : this.monthlyInputChartData
    },
    showInputOverTime () {
      return this.hasInputsOverTime && Object.keys(this.inputChartData).length > 0
    },
    showMediaSpend () {
      // The template reaches this as the v-else of Input Over Time, so the first check is what
      // that v-else already implies; it is spelled out here for the Admin tab.
      if (this.hasInputsOverTime) return false
      if (this.hasWeeklyDetailData) {
        return !!(this.v2Report && 'media_spend_weekly' in this.v2Report && this.v2Report.media_spend_weekly)
      }
      return !!(this.v2Report && 'media_spend' in this.v2Report)
    },
    showRoasAverageMedia () {
      return !!(this.hasRoasAverageMediaByFreq && this.v2Report && 'roi_unit_name' in this.v2Report)
    },
    showRoasAverageMediaLegacy () {
      return !!(this.v2Report && 'quarterly_roi_average_media' in this.v2Report)
    },
    showSaturationCurves () {
      return !!this.v2Report
    },
    canExportSaturationCurves () {
      return this.authUser.isAdmin || this.authUser.isScientist || this.authUser.isManager
    },
    showPricingEffect () {
      return !!(this.v2Report && 'effects' in this.v2Report && 'pricing' in this.v2Report.effects)
    },
    showDistributionEffect () {
      return !!(this.v2Report && 'effects' in this.v2Report && 'distribution' in this.v2Report.effects)
    },
    showSalesBySegment () {
      return !!(this.parsedModelReport && this.parsedModelReport.segments)
    },
    showMicroaudienceDifference () {
      return !!(this.parsedModelReport && this.parsedModelReport.microaudience && this.parsedModelReport.microaudience.difference)
    },
    showMicroaudienceComposition () {
      return !!(this.parsedModelReport && this.parsedModelReport.microaudience && this.parsedModelReport.microaudience.waves)
    },
    mediaSpendAndReturnData () {
      const empty = { channels: [], spend: {}, attributedSales: {} }
      if (!this.parsedModelReport || !this.v2Report) {
        return empty
      }
      const prettyMedia = this.client && this.client.pretty_names && this.client.pretty_names.media ? this.client.pretty_names.media : {}
      const toPretty = name => prettyMedia[name] || name
      const spend = {}
      const attributedSales = {}
      const hasByPeriod = 'media_spend' in this.v2Report && 'media_attributed_sales_decomposition_by_period' in this.v2Report
      if (hasByPeriod) {
        // Spend is intrinsically additive (dollars spent across weeks always sum), so aggregate it with 'sum' regardless
        // of the output's is_additive flag. The sum-vs-average decision only applies to the model output (attributed sales).
        const spendAggregated = this.aggregateSalesDecompositionByPeriod(this.v2Report.media_spend, 'sum')
        const salesAggregated = this.aggregateSalesDecompositionByPeriod(this.v2Report.media_attributed_sales_decomposition_by_period, this.salesDecompositionAggregationMode)
        spendAggregated.components.forEach(c => {
          spend[toPretty(c.name)] = c.value
        })
        salesAggregated.components.forEach(c => {
          attributedSales[toPretty(c.name)] = c.value
        })
      } else {
        // Legacy fallback for reports generated before the by-period fields were serialized:
        // spend from parsedModelReport.invest, attributed sales from v2.media_attributed_sales_decomposition_total.components.
        if (!this.parsedModelReport.invest || !('media_attributed_sales_decomposition_total' in this.v2Report)) {
          return empty
        }
        Object.entries(this.parsedModelReport.invest).forEach(entry => {
          spend[toPretty(entry[0])] = entry[1]
        })
        const attributedSalesRaw = this.v2Report.media_attributed_sales_decomposition_total
        let components
        if (attributedSalesRaw && attributedSalesRaw.components) {
          components = attributedSalesRaw.components
        } else if (attributedSalesRaw && attributedSalesRaw.outputs) {
          const firstKey = firstVisibleOutputName(this.client, attributedSalesRaw.outputs)
          components = firstKey ? attributedSalesRaw.outputs[firstKey].components : null
        }
        if (Array.isArray(components)) {
          components.forEach(c => {
            if (c && typeof c.value === 'number') {
              attributedSales[toPretty(c.name)] = c.value
            }
          })
        } else if (components && typeof components === 'object') {
          Object.entries(components).forEach(entry => {
            if (typeof entry[1] === 'number') {
              attributedSales[toPretty(entry[0])] = entry[1]
            }
          })
        }
      }
      const channels = Object.keys(spend).filter(c => c in attributedSales).sort((a, b) => a.localeCompare(b))
      return { channels: channels, spend: spend, attributedSales: attributedSales }
    },
    hasMediaSpendAndReturnData () {
      return this.mediaSpendAndReturnData.channels.length > 0
    },
    // True when the chart's attributed sales are per-period averages while its spend is a
    // period total, which is what the by-period branch above does for a non-additive output:
    // the resulting per-channel ROAS mixes the two scales. The legacy branch reads the
    // decomposition total and averages nothing, so it never carries the caveat.
    mediaSpendAndReturnAveraged () {
      if (this.salesDecompositionAggregationMode !== 'average') return false
      return !!(this.v2Report &&
        'media_spend' in this.v2Report &&
        'media_attributed_sales_decomposition_by_period' in this.v2Report)
    },
    modelFitColors () {
      return {
        'Prediction': '#90C273', // Green
        'Real': '#4B67B8', // Blue
        'Confidence': 'rgba(144, 194, 115, 0.2)', // Translucent green paired with Prediction
        'Confidence_lb': 'rgba(0, 0, 0, 0)', // Invisible — only used as stack baseline
        'Outliers': '#E53935', // Red
        'Epochs': '#A3C1DA' // Light blue, matches the epoch band border
      }
    },
    hasModelFitWeekly () {
      if (!this.v2Report || !('model_fit_weekly' in this.v2Report)) return false
      const weekly = this.v2Report.model_fit_weekly
      if (!weekly || typeof weekly !== 'object') return false
      return Object.keys(weekly).length > 0
    },
    isWeeklyModel () {
      return !!(this.client && this.client.reference_model && this.client.reference_model.config && this.client.reference_model.config.saturation_granularity === 'W')
    },
    modelFitGranularity: {
      get () {
        if (this.modelFitGranularityOverride !== null) {
          return this.modelFitGranularityOverride
        }
        if (this.isWeeklyModel && this.hasModelFitWeekly) {
          return 'weekly'
        }
        return 'monthly'
      },
      set (value) {
        this.modelFitGranularityOverride = value
      }
    },
    modelFitData () {
      const raw = (this.modelFitGranularity === 'weekly' && this.hasModelFitWeekly)
        ? this.v2Report.model_fit_weekly
        : this.v2Report.model_fit
      if (!raw) return raw
      const out = {}
      if ('Prediction' in raw) out.Prediction = { x: raw.Prediction.x, y: raw.Prediction.y }
      if ('Real' in raw) out.Real = raw.Real
      if ('Confidence_lb' in raw && raw.Confidence_lb && 'Confidence_ub' in raw && raw.Confidence_ub) {
        const lb = raw.Confidence_lb
        const ub = raw.Confidence_ub
        out.Confidence_lb = { x: lb.x, y: lb.y }
        out.Confidence = {
          x: ub.x,
          y: ub.y.map((upper, i) => upper - lb.y[i])
        }
      }
      if (out.Prediction) {
        const predIndex = {}
        for (let i = 0; i < out.Prediction.x.length; i++) {
          predIndex[out.Prediction.x[i]] = i
        }
        const buildSparse = (series) => {
          const x = []
          const y = []
          for (let i = 0; i < series.x.length; i++) {
            if (series.y[i] === 1 && series.x[i] in predIndex) {
              x.push(series.x[i])
              y.push(out.Prediction.y[predIndex[series.x[i]]])
            }
          }
          return { x: x, y: y }
        }
        if ('Outliers' in raw && raw.Outliers) {
          out.Outliers = buildSparse(raw.Outliers)
        }
        if ('Epochs' in raw && raw.Epochs) {
          const ex = raw.Epochs.x
          const ey = raw.Epochs.y
          const bands = []
          let runStart = -1
          for (let i = 0; i < ey.length; i++) {
            if (ey[i] === 1) {
              if (runStart === -1) runStart = i
            } else if (runStart !== -1) {
              bands.push([ex[runStart], ex[i]])
              runStart = -1
            }
          }
          if (runStart !== -1) {
            bands.push([ex[runStart], ex[ex.length - 1]])
          }
          if (bands.length > 0) {
            out.Epochs = { x: [], y: [], _epochBands: bands }
          }
        }
      }
      return out
    },
    modelFitXName () {
      return (this.modelFitGranularity === 'weekly' && this.hasModelFitWeekly) ? 'Weekly' : ''
    },
    modelFitNameTranslations () {
      return {
        'Prediction': this.$t('views.ModelReport.prediction'),
        'Real': this.$t('views.ModelReport.real'),
        'Confidence': this.$t('components.ModelReportComponent.confidence'),
        'Outliers': this.$t('components.ModelReportComponent.outliers'),
        'Epochs': this.$t('components.ModelReportComponent.epochs')
      }
    },
    modelFitLineStyles () {
      const styles = {
        Prediction: { smooth: false, type: 'solid', width: 0.7 },
        Real: { smooth: false, type: 'solid', width: 0, symbol: 'circle', symbolSize: 5, legendIcon: 'circle' }
      }
      const data = this.modelFitData
      if (data && 'Confidence' in data && 'Confidence_lb' in data) {
        styles.Confidence_lb = { smooth: false, width: 0, symbol: 'none', stack: 'confidence', hideInLegend: true, hideInTooltip: true }
        styles.Confidence = {
          smooth: false,
          width: 0,
          symbol: 'none',
          stack: 'confidence',
          areaStyle: { color: 'rgba(144, 194, 115, 0.2)' },
          tooltipPlusMinus: true,
          legendIcon: 'circle'
        }
      }
      if (data && 'Outliers' in data) {
        styles.Outliers = { seriesType: 'scatter', symbol: 'circle', symbolSize: 8, hideInTooltip: true, legendIcon: 'circle' }
      }
      if (data && 'Epochs' in data) {
        styles.Epochs = { smooth: false, type: 'solid', width: 0, symbol: 'none', hideInTooltip: true, legendIcon: 'circle' }
      }
      return JSON.stringify(styles)
    },
    hasTotalRoasByMedia () {
      if (!(this.client.type === 'automatic') || !this.v2Report || !('total_roas_by_media' in this.v2Report)) return false
      var roasData = this.v2Report.total_roas_by_media
      if (!roasData) return false
      return Object.values(roasData).some(function (v) { return Number(v) !== 0 })
    },
    totalRoasByMediaChartData () {
      if (!this.hasTotalRoasByMedia) return { x: [], y: [] }
      var roasData = this.v2Report.total_roas_by_media
      var prettyMedia = this.client && this.client.pretty_names && this.client.pretty_names.media ? this.client.pretty_names.media : {}
      var entries = Object.entries(roasData)
        .map(function (entry) { return [prettyMedia[entry[0]] || entry[0], entry[1]] })
        .sort(function (a, b) { return a[1] - b[1] })
      return {
        x: entries.map(function (e) { return e[0] }),
        y: entries.map(function (e) { return e[1] })
      }
    },
    channelColors () {
      if (!this.client || !this.client.channelColors) {
        return {}
      }
      return this.prettyTranslateNames(this.client.channelColors, [''], this.client.pretty_names ? this.client.pretty_names.media : {})
    },
    client: function () {
      // If shareParams provided, use that directly
      if (this.shareParams && this.shareParams.clientObject) {
        return this.shareParams.clientObject
      }
      // Otherwise use authenticated flow
      // TODO: Rethink data model. This is for Model Reports to auto update when report is done.
      // The problem is currentClientObject is not reactive to Firestore updates, but clients is.
      let clientObject = this.currentClientObject
      let updatedClientObject = this.clients.filter(client => client.id === this.currentClient)[0]
      if (updatedClientObject && clientObject) {
        if ('model_report' in updatedClientObject) {
          clientObject.model_report = updatedClientObject.model_report
        }
        if ('model_report_storage' in updatedClientObject) {
          clientObject.model_report_storage = updatedClientObject.model_report_storage
        }
        if ('model_report_summary' in updatedClientObject) {
          clientObject.model_report_summary = updatedClientObject.model_report_summary
        }
        if ('model_report_updated_at' in updatedClientObject) {
          clientObject.model_report_updated_at = updatedClientObject.model_report_updated_at
        }
      }
      return clientObject
    },
    isFetchingModelReport: function () {
      // True only while a Storage-backed report is in flight — distinguishes
      // "loading the JSON" from "no report exists yet (still building)".
      if (!this.client || !this.client.model_report_storage) return false
      return this.fetchedModelReportFor !== this.modelReportFetchKey
    },
    parsedModelReport: function () {
      // Prefer the Storage-backed report fetched asynchronously by the watcher below.
      // Falls back to inline string/object for legacy documents.
      if (!this.client) return null
      if (this.client.model_report_storage) {
        return this.fetchedModelReport
      }
      if (!this.client.model_report) return null
      if (typeof this.client.model_report === 'string') {
        try {
          return JSON.parse(this.client.model_report)
        } catch (e) {
          console.error('Failed to parse model_report:', e)
          return null
        }
      }
      return this.client.model_report
    },
    spend: function () {
      if (!this.parsedModelReport || !this.parsedModelReport.invest) {
        return 0
      }
      var spend = 0
      for (var key in this.parsedModelReport.invest) {
        spend += this.parsedModelReport.invest[key]
      }
      return spend
    },
    sales: function () {
      if (this.salesDecompositionV2Data) {
        return this.salesDecompositionV2Data.total
      }
      var sales = 0
      if (this.currentOutputObject && 'components' in this.currentOutputObject) {
        for (var key in this.currentOutputObject.components) {
          sales += this.currentOutputObject.components[key]
        }
      }
      return sales
    },
    roasTotal: function () {
      if (!this.parsedModelReport) return null
      if (!this.spend || this.spend === 0) return null

      let rawOutput = this.parsedModelReport
      if (this.isCustomOutput && this.customOutputObject) {
        rawOutput = this.customOutputObject
      } else if (!this.isAggregateOutput &&
          this.parsedModelReport.outputs && this.currentOutput in this.parsedModelReport.outputs) {
        rawOutput = this.parsedModelReport.outputs[this.currentOutput]
      }

      let components = null
      const v2Decomp = rawOutput.v2 && rawOutput.v2.sales_decomposition_total
      if (v2Decomp && v2Decomp.components) {
        components = v2Decomp.components
      } else if (v2Decomp && v2Decomp.outputs && Object.keys(v2Decomp.outputs).length > 0) {
        const firstKey = firstVisibleOutputName(this.client, v2Decomp.outputs)
        if (firstKey && v2Decomp.outputs[firstKey] && v2Decomp.outputs[firstKey].components) {
          components = v2Decomp.outputs[firstKey].components
        }
      } else if (rawOutput.components) {
        components = rawOutput.components
      }
      if (!components) return null

      // Components may use raw keys (marketing, Marketing, marketing_residual)
      // or already-translated names (Instant Marketing / Marketing Instantáneo / etc.).
      const localizedInstant = String(this.$t('components.simulationDetails.SalesDecomposition.instantMarketing')).toLowerCase()
      const localizedCarryOver = String(this.$t('components.simulationDetails.SalesDecomposition.carryOverMarketing')).toLowerCase()
      const isInstant = (name) => {
        if (name === 'marketing' || name === 'Marketing') return true
        const lower = String(name).toLowerCase()
        return lower.includes('instant marketing') || lower === localizedInstant
      }
      const isCarryOver = (name) => {
        if (name === 'marketing_residual') return true
        const lower = String(name).toLowerCase()
        return lower.includes('carry-over marketing') ||
               lower.includes('carry over marketing') ||
               lower.includes('carryover marketing') ||
               lower === localizedCarryOver
      }

      let marketingValue = 0
      let carryOverValue = 0
      let found = false

      if (Array.isArray(components)) {
        components.forEach(c => {
          if (isInstant(c.name)) {
            marketingValue += c.value
            found = true
          } else if (isCarryOver(c.name)) {
            carryOverValue += c.value
            found = true
          }
        })
      } else if (typeof components === 'object') {
        Object.entries(components).forEach(([key, value]) => {
          if (typeof value !== 'number') return
          if (isInstant(key)) {
            marketingValue += value
            found = true
          } else if (isCarryOver(key)) {
            carryOverValue += value
            found = true
          }
        })
      }

      if (!found) return null

      return (marketingValue + carryOverValue) / this.spend
    },
    // The composite unit of the ROAS, when it has one: the ratio of a KPI measured
    // in units the spend isn't (e.g. "units/USD"). It goes in the box label, like
    // the Spend and KPI boxes beside it, so the figure itself stays short.
    roasUnit: function () {
      return roasUnitLabel(this.currentOutputUnitName, this.spendUnitName)
    },
    roasTotalLabel: function () {
      const label = this.$t('components.ModelReportComponent.roasTotal')
      return this.roasUnit ? `${label} (${this.roasUnit})` : label
    },
    // A cross-unit ROAS is not the familiar return multiple, so the box flags it.
    // Nothing to warn about when the figure itself isn't shown.
    roasTotalWarnings: function () {
      if (this.roasTotal === null || !this.roasUnit) return []
      return [this.$t('components.ModelReportComponent.roasCrossUnitWarning', {
        kpi: this.customKpiName,
        unit: this.roasUnit
      })]
    },
    roasTotalFormatted: function () {
      if (this.roasTotal === null) return '-'
      return numbro(this.roasTotal).format({
        thousandSeparated: true,
        mantissa: roasMantissa(this.roasTotal),
        optionalMantissa: true,
        trimMantissa: true
      }) + roasSuffix(this.currentOutputUnitName, this.spendUnitName)
    },
    roasTotalFullFormatted: function () {
      if (this.roasTotal === null) return '-'
      return numbro(this.roasTotal).format({
        thousandSeparated: true,
        mantissa: roasMantissa(this.roasTotal) + 2,
        optionalMantissa: true,
        trimMantissa: true
      }) + roasSuffix(this.currentOutputUnitName, this.spendUnitName)
    },
    date_from: function () {
      return this.parsedModelReport ? this.parsedModelReport.start_date : null
    },
    date_to: function () {
      return this.parsedModelReport ? this.parsedModelReport.end_date : null
    },
    // The two halves of "can these outputs be added into a Total", kept apart so the Admin tab
    // can name which one is missing.
    outputsCombinableFlag: function () {
      const config = this.client && this.client.reference_model && this.client.reference_model.config
      const metadata = config && config.metadata
      return !metadata || metadata.combinable_outputs !== false
    },
    outputsAllAdditive: function () {
      const data = this.parsedModelReport
      if (!data || !('outputs' in data)) return true
      const config = this.client && this.client.reference_model && this.client.reference_model.config
      const configOutputs = config && config.outputs
      return Object.keys(data.outputs).every(name => {
        if (!configOutputs || !configOutputs[name]) return true
        return configOutputs[name].is_additive !== false
      })
    },
    outputs: function () {
      if (this.parsedModelReport && 'outputs' in this.parsedModelReport) {
        const data = this.parsedModelReport
        // KPIs hidden by the model's Display settings are left out, and so is the Total while
        // any is hidden (it adds up the hidden KPIs too).
        let outputs = {}
        Object.keys(data.outputs).forEach(name => {
          if (!isKpiHidden(this.client, name)) {
            outputs[name] = data.outputs[name]
          }
        })
        const combinable = this.outputsCombinableFlag && this.outputsAllAdditive
        if ('total' in data && combinable && !hasHiddenKpis(this.client)) {
          outputs['Total'] = { ...data }
          // A custom subset is offered exactly when the Total is: it adds up like it.
          if (Object.keys(data.outputs).length > 2) {
            outputs[CUSTOM_OUTPUT] = this.customOutputObject
          }
        }
        return outputs
      } else {
        return null
        // For testing, intead of null:
        /* let outputs = {
          'kpi1': { ...data },
          'kpi2': { ...data }
        }
        outputs['Total'] = { ...data }
        return outputs */
      }
    },
    // The selector's options, the custom subset included.
    outputNames: function () {
      if (this.outputs) {
        return Object.keys(this.outputs)
      } else {
        return []
      }
    },
    outputItems: function () {
      return this.outputNames.map(name => ({
        text: name === CUSTOM_OUTPUT ? this.$t('components.ModelReportComponent.customKpiOption') : name,
        value: name
      }))
    },
    // The model's visible KPIs, without the Total and the custom subset. Read off the report and
    // not off `outputs`, which builds the custom subset out of them.
    kpiNames: function () {
      const data = this.parsedModelReport
      if (!data || !data.outputs) return []
      return Object.keys(data.outputs).filter(name => !isKpiHidden(this.client, name))
    },
    isCustomOutput: function () {
      return this.currentOutput === CUSTOM_OUTPUT
    },
    // The Total and the custom subset add several KPIs up; null is the report's root, the Total.
    isAggregateOutput: function () {
      return !this.currentOutput || this.currentOutput === 'Total' || this.isCustomOutput
    },
    // The chosen KPIs that are still in the model, in the selector's order.
    customOutputNames: function () {
      return this.kpiNames.filter(name => this.customOutputs.includes(name))
    },
    // The custom subset's report: the sum of two or more KPIs, else the one KPI chosen, else the
    // Total.
    customOutputObject: function () {
      const data = this.parsedModelReport
      const names = this.customOutputNames
      if (names.length >= 2) return combineOutputs(data, names)
      if (names.length === 1) return data.outputs[names[0]]
      return { ...data }
    },
    // What names the custom subset in downloads, e.g. "Sales P1 + Sales P3".
    customOutputLabel: function () {
      return this.customOutputNames.length > 0
        ? this.customOutputNames.join(' + ')
        : this.$t('components.ModelReportComponent.customKpiOption')
    },
    // The selected option as named in downloads.
    currentOutputLabel: function () {
      return this.isCustomOutput ? this.customOutputLabel : this.currentOutput
    },
    currentOutputObject: function () {
      const basePathsComponents = [
        'v2.sales_decomposition_by_period',
        'v2.sales_decomposition_total.components'
      ]
      const basePaths = [
        'invest',
        'marketing',
        'decay',
        'saturation',
        'v2.media_mix',
        'v2.media_spend',
        'v2.media_attributed_sales_decomposition_by_period',
        'v2.media_attributed_sales_decomposition_total.components',
        'v2.quarterly_roi_average_media',
        'v2.media_attributed_sales_decomposition_total.components',
        'v2.total_roas_by_media'
      ]
      // Include all outputs (recursive versions of the basePaths at outputs.output_name)
      let paths = [].concat(basePaths)
      if (this.outputNames && this.outputNames.length > 0) {
        this.outputNames.forEach(output => {
          if (output !== 'Total' && output !== CUSTOM_OUTPUT) {
            paths = paths.concat(basePaths.map(path => {
              return `outputs.${output}.${path}`
            }))
          }
        })
      }
      let componentPaths = [].concat(basePathsComponents)
      if (this.outputNames && this.outputNames.length > 0) {
        this.outputNames.forEach(output => {
          if (output !== 'Total' && output !== CUSTOM_OUTPUT) {
            componentPaths = componentPaths.concat(basePathsComponents.map(path => {
              return `outputs.${output}.${path}`
            }))
          }
        })
      }
      if (this.currentOutput) {
        return this.prettyTranslateNames(this.prettyTranslateNames(this.outputs[this.currentOutput], basePaths, this.client.pretty_names ? this.client.pretty_names.media : {}), basePathsComponents, this.client.pretty_names ? this.client.pretty_names.components : {})
      } else if (this.parsedModelReport) {
        return this.prettyTranslateNames(this.prettyTranslateNames(this.parsedModelReport, paths, this.client.pretty_names ? this.client.pretty_names.media : {}), componentPaths, this.client.pretty_names ? this.client.pretty_names.components : {})
      } else {
        return {}
      }
    },
    saturationCurvesData: function () {
      // currentOutputObject already pretty-translates the direct children of `saturation`.
      // In the flat case those are the channels (correct). In the grouped case (a first-level
      // "Instant Marketing" sub-key) those are the group names, so the inner channels stay raw — here
      // we pretty-translate the channels inside each group so names/colors match the rest of
      // the report. When not grouped, the data is returned unchanged.
      const sat = this.currentOutputObject.saturation
      if (!sat) return sat
      if (Object.prototype.hasOwnProperty.call(sat, 'Instant Marketing')) {
        const mediaDict = this.client.pretty_names ? this.client.pretty_names.media : {}
        const out = {}
        for (const group in sat) {
          if (Object.prototype.hasOwnProperty.call(sat, group)) {
            // path '' translates the first-level keys (channels) of each group
            out[group] = this.prettyTranslateNames(sat[group], [''], mediaDict)
          }
        }
        return out
      }
      return sat
    },
    decayCurvesData: function () {
      // Same grouped-vs-flat handling as saturationCurvesData, for the decay object.
      // currentOutputObject pretty-translates the direct children of `decay`; in the grouped
      // case (a first-level "Instant Marketing" sub-key) those are the group names, so we re-translate
      // the channels inside each group. Flat data is returned unchanged.
      const decay = this.currentOutputObject.decay
      if (!decay) return decay
      if (Object.prototype.hasOwnProperty.call(decay, 'Instant Marketing')) {
        const mediaDict = this.client.pretty_names ? this.client.pretty_names.media : {}
        const out = {}
        for (const group in decay) {
          if (Object.prototype.hasOwnProperty.call(decay, group)) {
            // path '' translates the first-level keys (channels) of each group
            out[group] = this.prettyTranslateNames(decay[group], [''], mediaDict)
          }
        }
        return out
      }
      return decay
    },
    hasDecayGroups () {
      return this.decayCurvesData &&
        Object.prototype.hasOwnProperty.call(this.decayCurvesData, 'Instant Marketing')
    },
    decayGroupItems () {
      // {text, value} group labels, "Instant Marketing" first. The value keeps the real data
      // key: it is what the decay object is indexed by and what hasDecayGroups and the group
      // watcher compare against, so only the label is translated. The keys of a grouped decay
      // object are components, so they are named by the components dictionary and not by the
      // media one currentOutputObject applies to `decay`.
      if (!this.hasDecayGroups) return []
      const keys = Object.keys(this.decayCurvesData)
      return keys.slice()
        .sort((a, b) => (a === 'Instant Marketing' ? -1 : b === 'Instant Marketing' ? 1 : 0))
        .map(key => ({ text: this.decompGroupDisplayName(key), value: key }))
    },
    // The decay object actually rendered: the selected group, or the flat data.
    effectiveDecayData () {
      if (this.hasDecayGroups) {
        return this.decayCurvesData[this.selectedDecayGroup] || {}
      }
      return this.decayCurvesData
    },
    currentOutputUnitName: function () {
      // Add null safety check
      if (!this.client || !this.client.reference_model || !this.client.reference_model.config) {
        return 'USD'
      }
      if (this.isAggregateOutput) {
        return this.client.reference_model.config.sales_unit_name || 'USD'
      } else if ('outputs' in this.client.reference_model.config && this.currentOutput in this.client.reference_model.config.outputs) {
        return this.client.reference_model.config.outputs[this.currentOutput].sales_unit_name || 'USD'
      } else {
        console.log('Warning: sales_unit_name not found')
        return 'USD'
      }
    },
    spendUnitName: function () {
      if (this.client && this.client.reference_model && this.client.reference_model.config && 'invest_unit_name' in this.client.reference_model.config) {
        return this.client.reference_model.config.invest_unit_name
      } else {
        return 'USD'
      }
    },
    v2Report: function () {
      if ('v2' in this.currentOutputObject) {
        return this.currentOutputObject.v2
      } else {
        return false
      }
    },
    inputsOverTime: function () {
      if (!this.v2Report || !('inputs_over_time' in this.v2Report)) return null
      const o = this.v2Report.inputs_over_time
      if (!o || typeof o !== 'object') return null
      return o
    },
    hasInputsOverTime: function () {
      return !!this.inputsOverTime && Object.keys(this.inputsOverTime).length > 0
    },
    salesDecompositionHasAllVariables: function () {
      return !!this.v2Report && !!this.v2Report.sales_decomposition_by_period &&
        !!this.findAllVariablesKey(this.v2Report.sales_decomposition_by_period)
    },
    salesDecompositionWeeklyHasAllVariables: function () {
      return !!this.v2Report && !!this.v2Report.sales_decomposition_by_period_weekly &&
        !!this.findAllVariablesKey(this.v2Report.sales_decomposition_by_period_weekly)
    },
    salesDecompositionGroupItems: function () {
      return this.buildDecompGroupItems(this.v2Report ? this.v2Report.sales_decomposition_by_period : null)
    },
    salesDecompositionGroupItemsWeekly: function () {
      return this.buildDecompGroupItems(this.v2Report ? this.v2Report.sales_decomposition_by_period_weekly : null)
    },
    salesDecompositionChartConfig: function () {
      return this.buildDecompChartConfig(this.v2Report ? this.v2Report.sales_decomposition_by_period : null, this.selectedSalesDecompositionGroup)
    },
    salesDecompositionChartConfigWeekly: function () {
      return this.buildDecompChartConfig(this.v2Report ? this.v2Report.sales_decomposition_by_period_weekly : null, this.selectedSalesDecompositionGroupWeekly)
    },
    inputVariableNames: function () {
      return this.inputsOverTime ? Object.keys(this.inputsOverTime) : []
    },
    selectedInputCategories: function () {
      const o = this.inputsOverTime
      const v = this.selectedInputVariable
      if (!o || !v || !(v in o)) return {}
      const variable = o[v]
      if (!variable || typeof variable !== 'object' || !variable.categories) return {}
      return variable.categories
    },
    marketingSpendKey: function () {
      return '__marketing_spend__'
    },
    hasMarketingSpend: function () {
      if (!this.v2Report) return false
      return ('media_spend' in this.v2Report) || ('media_spend_weekly' in this.v2Report)
    },
    inputVariableItems: function () {
      // The first-level keys of `inputs_over_time` are components, so they are named by the
      // components dictionary — no path of currentOutputObject reaches them. The value keeps
      // the raw key: it indexes the data and inputChartYName compares it against fixed names.
      const items = []
      if (this.hasMarketingSpend) {
        items.push({ text: this.$t('components.ModelReportComponent.marketingSpend'), value: this.marketingSpendKey })
      }
      for (const name of this.inputVariableNames) {
        items.push({ text: this.componentDisplayName(name), value: name })
      }
      return items
    },
    // The selected variable as the selector spells it; it also names the exported image.
    selectedInputVariableLabel: function () {
      if (this.selectedInputVariable === this.marketingSpendKey) {
        return this.$t('components.ModelReportComponent.marketingSpend')
      }
      return this.componentDisplayName(this.selectedInputVariable)
    },
    inputChartYName: function () {
      const spendVariables = ['Comarketing', 'Competition Marketing']
      if (this.selectedInputVariable === this.marketingSpendKey || spendVariables.indexOf(this.selectedInputVariable) !== -1) {
        return `Spend (${this.spendUnitName})`
      }
      return ''
    },
    selectedInputIsAdditive: function () {
      if (this.selectedInputVariable === this.marketingSpendKey) return true
      const o = this.inputsOverTime
      const v = this.selectedInputVariable
      if (!o || !v || !(v in o) || !o[v]) return true
      return o[v].is_additive !== false
    },
    monthlyInputChartData: function () {
      if (this.selectedInputVariable === this.marketingSpendKey) {
        return (this.v2Report && this.v2Report.media_spend) ? this.v2Report.media_spend : {}
      }
      return this.selectedInputCategories
    },
    weeklyInputChartData: function () {
      if (this.selectedInputVariable === this.marketingSpendKey) {
        return (this.v2Report && this.v2Report.media_spend_weekly) ? this.v2Report.media_spend_weekly : {}
      }
      return this.selectedInputCategories
    },
    roasAverageByFreqObj: function () {
      if (!this.v2Report || !('roas_average_total_by_freq' in this.v2Report)) return null
      return this.v2Report.roas_average_total_by_freq
    },
    normalizedRoasAverageByFreq: function () {
      return this.normalizeByFreq(this.roasAverageByFreqObj)
    },
    hasRoasAverageByFreq: function () {
      const o = this.normalizedRoasAverageByFreq
      if (!o) return false
      return ['weekly', 'monthly', 'quarterly', 'yearly'].some(k => k in o && o[k])
    },
    roasAverageMediaByFreqObj: function () {
      if (!this.v2Report || !('roas_average_media_by_freq' in this.v2Report)) return null
      return this.v2Report.roas_average_media_by_freq
    },
    normalizedRoasAverageMediaByFreq: function () {
      const o = this.roasAverageMediaByFreqObj
      if (!o || typeof o !== 'object') return null
      const out = {}
      for (const channel of Object.keys(o)) {
        const norm = this.normalizeByFreq(o[channel])
        if (norm && Object.keys(norm).length) out[channel] = norm
      }
      return Object.keys(out).length ? out : null
    },
    roasAverageMediaAvailableFreqs: function () {
      const o = this.normalizedRoasAverageMediaByFreq
      const set = new Set()
      if (!o) return set
      for (const channel of Object.keys(o)) {
        for (const f of ['weekly', 'monthly', 'quarterly', 'yearly']) {
          if (f in o[channel] && o[channel][f]) set.add(f)
        }
      }
      return set
    },
    hasRoasAverageMediaByFreq: function () {
      return this.roasAverageMediaAvailableFreqs.size > 0
    },
    roasAverageMediaGranularityItems: function () {
      const freqs = this.roasAverageMediaAvailableFreqs
      const order = ['weekly', 'monthly', 'quarterly', 'yearly']
      return order.filter(k => freqs.has(k)).map(k => ({
        text: this.$t('components.ModelReportComponent.' + k),
        value: k
      }))
    },
    selectedRoasAverageMediaData: function () {
      const o = this.normalizedRoasAverageMediaByFreq
      const g = this.roasAverageMediaGranularity
      if (!o || !g) return {}
      const out = {}
      for (const channel of Object.keys(o)) {
        if (g in o[channel] && o[channel][g]) out[channel] = o[channel][g]
      }
      return out
    },
    roasAverageMediaChartPeriod: function () {
      switch (this.roasAverageMediaGranularity) {
        case 'weekly': return 'week'
        case 'monthly': return 'month'
        case 'yearly': return 'year'
        default: return ''
      }
    },
    hasWeeklyDetailData: function () {
      if (!this.isWeeklyModel || !this.v2Report) return false
      const expected = [
        'seasonality_weekly',
        'media_spend_weekly',
        'media_mix_weekly',
        'sales_decomposition_by_period_weekly',
        'media_attributed_sales_decomposition_by_period_weekly'
      ]
      let n = 0
      for (const k of expected) {
        if (k in this.v2Report && this.v2Report[k]) {
          n++
          if (n >= 3) return true
        }
      }
      return false
    },
    roasAveragePeriodGranularityItems: function () {
      const o = this.normalizedRoasAverageByFreq
      if (!o) return []
      const order = ['weekly', 'monthly', 'quarterly', 'yearly']
      return order.filter(k => k in o && o[k]).map(k => ({
        text: this.$t('components.ModelReportComponent.' + k),
        value: k
      }))
    },
    selectedRoasAveragePeriodData: function () {
      const o = this.normalizedRoasAverageByFreq
      if (!o || !this.roasAveragePeriodGranularity || !(this.roasAveragePeriodGranularity in o)) {
        return { x: [], y: [] }
      }
      return o[this.roasAveragePeriodGranularity]
    },
    roasAveragePeriodBarChartPeriod: function () {
      switch (this.roasAveragePeriodGranularity) {
        case 'monthly': return 'month'
        case 'yearly': return 'year'
        case 'weekly': return 'week'
        default: return 'month'
      }
    },
    roasAveragePeriodExportLabel: function () {
      const labels = {
        weekly: 'Weekly Average Total ROAS',
        monthly: 'Monthly Average Total ROAS',
        quarterly: 'Quarterly Average Total ROAS',
        yearly: 'Yearly Average Total ROAS'
      }
      return labels[this.roasAveragePeriodGranularity] || 'Average Total ROAS'
    },
    roasAveragePeriodShowLabels: function () {
      const data = this.selectedRoasAveragePeriodData
      if (!data || !Array.isArray(data.x)) return true
      return data.x.length <= 20
    },
    salesDecompositionAggregationMode: function () {
      const reportOutputs = this.parsedModelReport && this.parsedModelReport.outputs
      const configOutputs = this.client && this.client.reference_model && this.client.reference_model.config && this.client.reference_model.config.outputs
      const isOutputAdditive = (outputName) => {
        if (!configOutputs || !configOutputs[outputName]) return true
        return configOutputs[outputName].is_additive !== false
      }
      if (!this.isAggregateOutput) {
        return isOutputAdditive(this.currentOutput) ? 'sum' : 'average'
      }
      const realOutputs = this.isCustomOutput
        ? this.customOutputNames
        : (reportOutputs ? Object.keys(reportOutputs) : [])
      const allAdditive = realOutputs.every(name => isOutputAdditive(name))
      return allAdditive ? 'sum' : 'average'
    },
    // The model's custom component names, as configured in Settings > Custom names > Component
    // names: { rawNameInTheReport: alias }. Only some paths of the report are pretty-translated on
    // the way in (see currentOutputObject), so a name read off one path may be raw while the same
    // component read off another is already aliased; these two dictionaries are what lets a
    // comparison between them be independent of that.
    componentPrettyNamesDict: function () {
      return this.client && this.client.pretty_names && this.client.pretty_names.components
        ? this.client.pretty_names.components
        : {}
    },
    // The names inside a drill-down group: media channels under "Instant Marketing", components
    // under any other group ("Market Timing Effects", "Distribution", ...). Neither is reached by
    // currentOutputObject, so the stacked bar names them itself, and it needs both dictionaries at
    // once - which one applies depends on the group, known only at render time. A fresh object:
    // writing into either dictionary would edit the store's own copy. Media wins a key present in
    // both, which is what the drill into Marketing used to get.
    decompDrillPrettyNamesDict: function () {
      const merged = {}
      const componentsDict = this.componentPrettyNamesDict
      Object.keys(componentsDict).forEach(raw => { merged[raw] = componentsDict[raw] })
      const mediaDict = (this.client && this.client.pretty_names && this.client.pretty_names.media)
        ? this.client.pretty_names.media
        : {}
      Object.keys(mediaDict).forEach(raw => { merged[raw] = mediaDict[raw] })
      return merged
    },
    componentRawNamesByPrettyName: function () {
      const reverse = {}
      const dict = this.componentPrettyNamesDict
      Object.keys(dict).forEach(raw => { reverse[dict[raw]] = raw })
      return reverse
    },
    // The full (flat) component-level decomposition: the "All_Variables" sub-key for nested
    // reports, or the object itself for legacy flat reports. Used by the donut/total so they
    // always show the complete decomposition regardless of the stacked-bar drill selection.
    salesDecompositionByPeriodFlat: function () {
      const byPeriod = this.v2Report ? this.v2Report.sales_decomposition_by_period : null
      if (!byPeriod || typeof byPeriod !== 'object') return null
      const allVariablesKey = this.findAllVariablesKey(byPeriod)
      return allVariablesKey ? byPeriod[allVariablesKey] : byPeriod
    },
    salesDecompositionV2Data: function () {
      if (!this.v2Report || !('sales_decomposition_by_period' in this.v2Report)) {
        return null
      }
      const aggregated = this.aggregateSalesDecompositionByPeriod(this.salesDecompositionByPeriodFlat, this.salesDecompositionAggregationMode)
      const totalComponents = this.v2Report.sales_decomposition_total && Array.isArray(this.v2Report.sales_decomposition_total.components)
        ? this.v2Report.sales_decomposition_total.components
        : null
      if (!totalComponents) {
        return aggregated
      }
      // sales_decomposition_total.components defines the canonical display order; sales_decomposition_by_period's
      // key order is not a contract. Reorder aggregated values to match, and warn on any set mismatch.
      // The two sides do not always spell a component the same way: currentOutputObject pretty-translates
      // the direct children of sales_decomposition_by_period, which in a nested (All_Variables) report are
      // the drill-down groups and not the components, while total.components is translated either way. So
      // each name is resolved through the components dictionary, in both directions, before it can be
      // declared missing — otherwise every aliased component falls through to the fallback below and the
      // canonical order is lost exactly when the model has custom component names.
      const valueByName = {}
      aggregated.components.forEach(c => { valueByName[c.name] = c.value })
      const resolveComponentName = (name) => {
        if (name in valueByName) return name
        const raw = this.componentRawNamesByPrettyName[name]
        if (raw && raw in valueByName) return raw
        const pretty = this.componentPrettyNamesDict[name]
        if (pretty && pretty in valueByName) return pretty
        return null
      }
      const ordered = []
      const seen = {}
      totalComponents.forEach(c => {
        const key = resolveComponentName(c.name)
        if (key !== null) {
          ordered.push({ name: c.name, value: valueByName[key] })
          seen[key] = true
        } else {
          console.log(`Warning: sales decomposition component '${c.name}' present in sales_decomposition_total.components but missing from sales_decomposition_by_period`)
        }
      })
      aggregated.components.forEach(c => {
        if (!seen[c.name]) {
          console.log(`Warning: sales decomposition component '${c.name}' present in sales_decomposition_by_period but missing from sales_decomposition_total.components`)
          ordered.push(c)
        }
      })
      return { components: ordered, total: aggregated.total }
    },
    decayChartGranularity: function () {
      // Add null safety check
      if (!this.currentClientObject || !this.currentClientObject.reference_model || !this.currentClientObject.reference_model.config) {
        return this.$t('views.ModelReport.periods')
      }
      if (this.currentClientObject.reference_model.config.decay_granularity === 'M') {
        return this.$t('views.ModelReport.months')
      } else if (this.currentClientObject.reference_model.config.decay_granularity === 'W') {
        return this.$t('views.ModelReport.weeks')
      }
      return this.$t('views.ModelReport.periods')
    },
    // AI Summary
    formattedVersionDates () {
      return this.aiSummaryVersions.map(v => {
        return moment(v.timestamp).format('MMM D, YYYY h:mm a')
      })
    },
    versionItems () {
      return this.aiSummaryVersions.map((version, index) => ({
        text: `Version ${index + 1}`,
        value: index,
        date: version.timestamp,
        fullText: version.text,
        isFavorite: version.isFavorite
      }))
    },
    // Everything this view decided not to render, with the reason and the condition that failed.
    // Built by negating the show* computeds the template itself uses, never by re-deriving the
    // rules. A hidden tab contributes one row for the tab instead of one per section inside it.
    hiddenSections () {
      const list = []
      if (!this.parsedModelReport) return list
      const t = (key, params) => this.$t('components.ModelReportComponent.' + key, params || {})
      const add = (group, name, reason, condition, params) => {
        list.push({ group: group, name: name, reason: reason, condition: condition, params: params || {} })
      }
      const header = t('hiddenGroupHeader')
      const tabsGroup = t('hiddenGroupTabs')
      const summary = t('summary')
      const drivers = t('driversPerformance')
      const segmentsGroup = t('segmentsDetail')
      const saturationGroup = this.$t('components.SaturationCurves.saturationCurves')
      // Drivers Performance renders one of two variants; name the keys of the active one.
      const suffix = this.hasWeeklyDetailData ? '_weekly' : ''
      const decompositionName = t('salesDecompositionByPeriod', { kpi: this.customKpiName })

      // --- Header ---
      if (!this.outputs) {
        add(header, t('kpiSelector'), 'missingData', 'outputs')
      } else if (!('total' in this.parsedModelReport)) {
        add(header, t('totalKpiOption'), 'missingData', 'total')
      } else if (!this.outputsCombinableFlag) {
        add(header, t('totalKpiOption'), 'clientSetting', 'reference_model.config.metadata.combinable_outputs === false')
      } else if (!this.outputsAllAdditive) {
        add(header, t('totalKpiOption'), 'notAdditive', 'reference_model.config.outputs[*].is_additive === false')
      } else if (hasHiddenKpis(this.client)) {
        add(header, t('totalKpiOption'), 'clientSetting', 'client.hidden_kpis')
      }
      if (this.outputs) {
        hiddenKpis(this.client).forEach(name => {
          if (name in this.parsedModelReport.outputs) {
            add(header, t('kpiOption', { kpi: name }), 'clientSetting', 'client.hidden_kpis')
          }
        })
      }
      if (!this.showManageAccessButton) {
        add(header, t('manageAccess'), 'role', 'isAdmin | isManager | isSupervisor | isModelAccessManager')
      }
      if (!this.showAiSummary) {
        const byFlag = !this.client.enableAI
        add(header, this.$t('components.AI.AiSummary.aiSummary'), byFlag ? 'clientSetting' : 'role',
          byFlag ? 'client.enableAI' : 'isAdmin | isScientist')
      }
      if (this.roasTotal === null) {
        add(header, t('totalRoasValue'), 'notApplicable',
          (!this.spend || this.spend === 0) ? 'invest = 0' : 'no marketing component in sales_decomposition_total')
      }

      // --- Tabs ---
      if (!this.showSegmentsTab) {
        add(tabsGroup, t('segmentsDetail'), 'missingData', 'segments / microaudience')
      }

      // --- Summary ---
      const isEkrm = this.client.type === 'ekrm'
      if (!this.showModelFit) {
        add(summary, t('modelFit'), isEkrm ? 'modelType' : 'missingData',
          isEkrm ? "client.type === 'ekrm'" : 'v2.model_fit')
      } else if (!this.hasModelFitWeekly) {
        add(summary, t('modelFitGranularityToggle'), 'missingData', 'v2.model_fit_weekly')
      }
      if (!this.hasTotalRoasByMedia) {
        if (!this.showModelFit) {
          add(summary, t('roas'), 'parentHidden', t('modelFit'))
        } else if (this.client.type !== 'automatic') {
          add(summary, t('roas'), 'modelType', "client.type !== 'automatic'")
        } else if (!this.v2Report || !('total_roas_by_media' in this.v2Report)) {
          add(summary, t('roas'), 'missingData', 'v2.total_roas_by_media')
        } else {
          add(summary, t('roas'), 'allZero', 'v2.total_roas_by_media')
        }
      }
      if (!this.showModelQuality) {
        if (isEkrm) {
          add(summary, t('modelQuality'), 'modelType', "client.type === 'ekrm'")
        } else if (this.isExternalUser) {
          add(summary, t('modelQuality'), 'role', 'isExternalUser')
        } else {
          add(summary, t('modelQuality'), 'missingData', 'v2.model_quality')
        }
      }
      if (!this.hasMediaSpendAndReturnData) {
        add(summary, t('mediaSpendAndReturn'), 'noChannels', 'v2.media_spend ∩ v2.media_attributed_sales_decomposition_by_period')
      }
      if (!this.showRoasAveragePeriod && !this.showRoasAveragePeriodLegacy) {
        const noUnit = !this.v2Report || !('roi_unit_name' in this.v2Report)
        add(summary, t('averageRoasByPeriod'), 'missingData',
          noUnit ? 'v2.roi_unit_name' : 'v2.roas_average_total_by_freq / v2.quarterly_roi_average_total')
      }

      // --- Drivers Performance ---
      if (!this.showSalesDecompositionByPeriod) {
        add(drivers, decompositionName, 'missingData', 'v2.sales_decomposition_by_period' + suffix)
      } else if (!this.showSalesDecompositionDrillDown) {
        add(drivers, t('salesDecompositionDrillDown'), 'missingData', 'v2.sales_decomposition_by_period' + suffix + '.All_Variables')
      }
      if (!this.showSeasonalityEffects) {
        add(drivers, t('seasonalityEffects'),
          this.salesDecompositionHasAllVariables ? 'drillDown' : 'missingData',
          this.salesDecompositionHasAllVariables ? 'v2.sales_decomposition_by_period.All_Variables' : 'v2.seasonality' + suffix,
          { section: decompositionName })
      }
      if (!this.showMediaAttributionByPeriod) {
        add(drivers, t('mediaAttributionByPeriod'),
          this.salesDecompositionHasAllVariables ? 'drillDown' : 'missingData',
          this.salesDecompositionHasAllVariables ? 'v2.sales_decomposition_by_period.All_Variables' : 'v2.media_attributed_sales_decomposition_by_period' + suffix,
          { section: decompositionName })
      }
      if (!this.showMediaMix) {
        add(drivers, t('mediaMix'),
          this.showInputOverTime ? 'replacedBy' : 'missingData',
          this.showInputOverTime ? 'inputs_over_time' : 'v2.media_mix' + suffix,
          { section: t('inputOverTime') })
      }
      if (!this.showInputOverTime) {
        add(drivers, t('inputOverTime'),
          this.hasInputsOverTime ? 'emptyData' : 'missingData',
          this.hasInputsOverTime ? 'v2.inputs_over_time[' + this.selectedInputVariableLabel + '].categories' : 'v2.inputs_over_time')
      }
      if (!this.showMediaSpend) {
        add(drivers, t('mediaSpend'),
          this.hasInputsOverTime ? 'replacedBy' : 'missingData',
          this.hasInputsOverTime ? 'inputs_over_time' : 'v2.media_spend' + suffix,
          { section: t('inputOverTime') })
      }
      if (!this.showRoasAverageMedia && !this.showRoasAverageMediaLegacy) {
        const noUnit = !this.v2Report || !('roi_unit_name' in this.v2Report)
        add(drivers, t('averageRoasByMedia'), 'missingData',
          noUnit ? 'v2.roi_unit_name' : 'v2.roas_average_media_by_freq / v2.quarterly_roi_average_media')
      }
      if (!this.showSaturationCurves) {
        add(drivers, saturationGroup, 'missingData', 'v2')
      }
      if (!this.showPricingEffect) {
        add(drivers, t('pricingEffect'), 'missingData', 'v2.effects.pricing')
      }
      if (!this.showDistributionEffect) {
        add(drivers, t('distributionEffect'), 'missingData', 'v2.effects.distribution')
      }

      // --- Saturation Curves internals, only when the section itself renders ---
      if (this.showSaturationCurves) {
        hiddenSaturationSections(this.saturationCurvesData, {
          enableGroupSelector: true,
          enableImpressionsModel: this.client.enableImpressionsModel,
          canExportCurves: this.canExportSaturationCurves
        }).forEach(row => {
          add(saturationGroup, this.$t(row.nameKey), row.reason, row.condition, row.params)
        })
      }

      // --- Segments Detail, only when the tab itself is reachable ---
      if (this.showSegmentsTab) {
        if (!this.showSalesBySegment) {
          add(segmentsGroup, t('salesBySegment'), 'missingData', 'segments')
        }
        if (!this.showMicroaudienceDifference) {
          add(segmentsGroup, t('microaudienceDifference'), 'missingData', 'microaudience.difference')
        }
        if (!this.showMicroaudienceComposition) {
          add(segmentsGroup, t('microaudienceComposition'), 'missingData', 'microaudience.waves')
        }
      }
      return list
    }
  },
  watch: {
    // The report loads after creation, and the list changes when the model's hidden KPIs do:
    // keep the selection on an offered KPI, defaulting to the last one (the Total when offered).
    // Leaving it null would render the report's root, i.e. the Total, even when it is hidden.
    outputNames: {
      immediate: true,
      handler: function (names) {
        if (names.length === 0) {
          this.currentOutput = null
        } else if (!this.currentOutput || !names.includes(this.currentOutput)) {
          const offered = names.filter(name => name !== CUSTOM_OUTPUT)
          this.currentOutput = offered[offered.length - 1]
        }
      }
    },
    // Opening the custom subset starts it from the KPI that was selected, when it was one.
    currentOutput: function (value, previous) {
      if (value === CUSTOM_OUTPUT && this.customOutputs.length === 0 && this.kpiNames.includes(previous)) {
        this.customOutputs = [previous]
      }
    },
    '$route.params': {
      handler: function () {
        // Only watch route params when not in share mode
        if (!this.shareParams && this.$route.params.client) {
          this.setCurrentClient(this.$route.params.client)
        }
      },
      immediate: true
    },
    modelReportFetchKey: {
      immediate: true,
      handler: function (newKey) {
        if (!newKey) {
          this.fetchedModelReport = null
          this.fetchedModelReportFor = null
          return
        }
        if (newKey === this.fetchedModelReportFor) return
        const target = newKey
        loadModelReportFromClient(this.client)
          .then(report => {
            // Only commit if no newer fetch has been started in the meantime.
            if (this.modelReportFetchKey === target) {
              this.fetchedModelReport = report
              this.fetchedModelReportFor = target
            }
          })
          .catch(err => {
            console.error('Failed to load model_report from Storage:', err)
            if (this.modelReportFetchKey === target) {
              this.fetchedModelReport = null
              this.fetchedModelReportFor = target
            }
          })
      }
    },
    hasRoasAverageByFreq: {
      immediate: true,
      handler: function (avail) {
        if (!avail) {
          this.roasAveragePeriodGranularity = null
          return
        }
        const o = this.normalizedRoasAverageByFreq
        if (this.roasAveragePeriodGranularity && o && (this.roasAveragePeriodGranularity in o) && o[this.roasAveragePeriodGranularity]) return
        const priority = ['quarterly', 'monthly', 'weekly', 'yearly']
        this.roasAveragePeriodGranularity = priority.find(k => o && (k in o) && o[k]) || null
      }
    },
    hasRoasAverageMediaByFreq: {
      immediate: true,
      handler: function (avail) {
        if (!avail) {
          this.roasAverageMediaGranularity = null
          return
        }
        const freqs = this.roasAverageMediaAvailableFreqs
        if (this.roasAverageMediaGranularity && freqs.has(this.roasAverageMediaGranularity)) return
        const priority = ['quarterly', 'monthly', 'weekly', 'yearly']
        this.roasAverageMediaGranularity = priority.find(k => freqs.has(k)) || null
      }
    },
    hasInputsOverTime: {
      immediate: true,
      handler: function (avail) {
        if (!avail) {
          this.selectedInputVariable = null
          return
        }
        const values = this.inputVariableItems.map(i => i.value)
        if (this.selectedInputVariable && values.indexOf(this.selectedInputVariable) !== -1) return
        this.selectedInputVariable = values.length ? values[0] : null
      }
    },
    hasDecayGroups: {
      immediate: true,
      handler: function (grouped) {
        if (!grouped) {
          this.selectedDecayGroup = null
          return
        }
        const items = this.decayGroupItems
        if (this.selectedDecayGroup && items.some(i => i.value === this.selectedDecayGroup)) return
        this.selectedDecayGroup = items.some(i => i.value === 'Instant Marketing') ? 'Instant Marketing' : (items.length ? items[0].value : null)
      }
    },
    salesDecompositionGroupItems: {
      immediate: true,
      handler: function (items) {
        // Reset to the first available group (synthetic "Marketing" when present, otherwise
        // "All_Variables") whenever the available groups change (e.g. switching client/report)
        // and the current selection is no longer valid. Skip while items are empty so the
        // initial "Marketing" default survives until the report data has loaded.
        if (items.length && !items.some(i => i.value === this.selectedSalesDecompositionGroup)) {
          this.selectedSalesDecompositionGroup = items[0].value
        }
      }
    },
    salesDecompositionGroupItemsWeekly: {
      immediate: true,
      handler: function (items) {
        if (items.length && !items.some(i => i.value === this.selectedSalesDecompositionGroupWeekly)) {
          this.selectedSalesDecompositionGroupWeekly = items[0].value
        }
      }
    },
    currentClientObject: {
      handler: function (newVal) {
        // Update AI summary when client data becomes available
        if (newVal && this.editedAISummary === this.$t('components.ModelReportComponent.aiSummaryDefault')) {
          this.editedAISummary = this.initialAISummary
        }
      },
      immediate: true
    }
  },
  created: function () {
    // Initialize editedAISummary with translated default
    this.editedAISummary = this.$t('components.ModelReportComponent.aiSummaryDefault')
    // Reload user clients when there's no Model Report, to refresh when it's calculated
    // Only when a PKL has been uploaded for the model
    // TODO: This is a hack, Firebase should automatically manage this, as commented elsewhere. Pending fix in the general client loading arch of ngapp
    // Skip this polling when in share mode
    if (!this.shareParams) {
      let nTries = 0
      const intervalId = setInterval(() => {
        nTries++
        if (this.currentClientObject && this.currentClientObject.reference_model && this.currentClientObject.reference_model.pkl && this.client && !this.parsedModelReport && !this.authUser.isManager && !this.authUser.isAdmin && !this.authUser.isSupervisor && this.authUser.clients && this.authUser.clients.length > 0) {
          this.loadClients({
            clients: this.authUser.clients
          })
          console.log('Reloaded clients')
        } else {
          clearInterval(intervalId)
        }
        if (nTries > 12) { // 12 tries, every 10 minutes ~ 2 hours
          clearInterval(intervalId)
        }
      }, 600000) // Every 10 minutes
    }
    // Initialize first version of AI Summary - will be updated when client data loads
    this.aiSummaryVersions = [{
      text: this.editedAISummary,
      timestamp: new Date(),
      isFavorite: true // First version is favorite by default
    }]
  },
  components: {
    ModelVersionBadge,
    NgtorchModelChips,
    SalesDecomposition,
    MediaSpendAndReturn,
    SalesBySegment,
    ChannelCurvesChartMulti,
    TextBox,
    TextBoxMetric,
    QualityGauge,
    MicroaudienceComposition,
    MicroaudienceDifference,
    RiverChart,
    ScatterChart,
    BarChart,
    StackedBarChart,
    ModelAccessManager,
    AiSummary,
    SaturationCurves,
    HiddenSectionsReport
  }
}
